import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const Exchange = () => {
  const { user, updateUser } = useAuth();
  const { success, error } = useNotification();
  const [exchangeRate, setExchangeRate] = useState(10);
  const [usdAmount, setUsdAmount] = useState('');
  const [creditsAmount, setCreditsAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exchanging, setExchanging] = useState(false);

  useEffect(() => {
    fetchExchangeRate();
  }, []);

  useEffect(() => {
    if (usdAmount && !isNaN(usdAmount) && usdAmount > 0) {
      setCreditsAmount(Math.floor(parseFloat(usdAmount) * exchangeRate));
    } else {
      setCreditsAmount(0);
    }
  }, [usdAmount, exchangeRate]);

  const fetchExchangeRate = async () => {
    try {
      const response = await axios.get('/api/credits/exchange-rate');
      setExchangeRate(response.data.rate);
    } catch (err) {
      console.error('Failed to fetch exchange rate:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExchange = async (e) => {
    e.preventDefault();
    
    if (!usdAmount || parseFloat(usdAmount) <= 0) {
      error('Please enter a valid USD amount');
      return;
    }

    if (parseFloat(usdAmount) > user.balance) {
      error('Insufficient USD balance');
      return;
    }

    setExchanging(true);
    
    try {
      const response = await axios.post('/api/credits/exchange', {
        usdAmount: parseFloat(usdAmount),
        creditsToAdd: creditsAmount
      });
      
      // Update user data in context
      updateUser(response.data.user);
      
      success(`Successfully exchanged $${usdAmount} for ${creditsAmount} credits!\nNew balance: $${response.data.balances.usd}, ${response.data.balances.credits} credits`);
      
      // Reset form
      setUsdAmount('');
      setCreditsAmount(0);
    } catch (err) {
      error(err.response?.data?.message || 'Exchange failed');
    } finally {
      setExchanging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">Currency Exchange</h1>
        <p className="text-gray-400">
          Convert your USD balance to credits for purchasing license keys.
        </p>
        <div className="mt-4 p-3 bg-blue-600/20 border border-blue-500/50 rounded-lg">
          <div className="flex items-center text-blue-400">
            <span className="text-lg mr-2">💱</span>
            <span className="font-semibold">Current Exchange Rate: 1 USD = {exchangeRate} credits</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exchange Form */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Exchange USD to Credits</h2>
          
          {/* Current Balance */}
          <div className="bg-dark-200 p-4 rounded-lg mb-6">
            <h3 className="text-white font-semibold mb-3">Your Current Balance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-green-600/10 rounded">
                <div className="text-green-400 font-semibold text-lg">${user?.balance?.toFixed(2) || '0.00'}</div>
                <div className="text-xs text-gray-400">USD Balance</div>
              </div>
              <div className="text-center p-3 bg-blue-600/10 rounded">
                <div className="text-blue-400 font-semibold text-lg">{user?.credits || 0}</div>
                <div className="text-xs text-gray-400">Credits</div>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleExchange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                USD Amount to Exchange
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={user?.balance || 0}
                  value={usdAmount}
                  onChange={(e) => setUsdAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                  placeholder="0.00"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Available: ${user?.balance?.toFixed(2) || '0.00'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Credits You Will Receive
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={creditsAmount}
                  readOnly
                  className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white cursor-not-allowed"
                />
                <span className="absolute right-3 top-3 text-gray-400">credits</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Rate: 1 USD = {exchangeRate} credits
              </p>
            </div>

            {usdAmount && creditsAmount > 0 && (
              <div className="bg-dark-200 p-4 rounded-lg space-y-2">
                <h4 className="text-white font-semibold mb-2">Exchange Summary</h4>
                <div className="flex justify-between">
                  <span className="text-gray-400">USD to exchange:</span>
                  <span className="text-red-400">-${parseFloat(usdAmount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Credits to receive:</span>
                  <span className="text-green-400">+{creditsAmount}</span>
                </div>
                <div className="flex justify-between border-t border-gray-600 pt-2">
                  <span className="text-gray-400">New USD balance:</span>
                  <span className="text-white">${(user?.balance - parseFloat(usdAmount)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">New credits balance:</span>
                  <span className="text-white">{(user?.credits || 0) + creditsAmount}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={!usdAmount || creditsAmount <= 0 || exchanging || parseFloat(usdAmount) > (user?.balance || 0)}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                usdAmount && creditsAmount > 0 && parseFloat(usdAmount) <= (user?.balance || 0) && !exchanging
                  ? 'btn-neon' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {exchanging ? (
                <div className="flex items-center justify-center">
                  <div className="spinner mr-2"></div>
                  Processing Exchange...
                </div>
              ) : !usdAmount || creditsAmount <= 0 ? (
                'Enter USD Amount to Exchange'
              ) : parseFloat(usdAmount) > (user?.balance || 0) ? (
                'Insufficient USD Balance'
              ) : (
                `Exchange $${parseFloat(usdAmount).toFixed(2)} for ${creditsAmount} credits`
              )}
            </button>
          </form>
        </div>

        {/* Exchange Information */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Exchange Information</h2>
          
          <div className="space-y-4">
            <div className="bg-dark-200 p-4 rounded-lg">
              <h3 className="text-white font-semibold mb-2">How It Works</h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• Convert your USD balance to credits</li>
                <li>• Use credits to purchase license keys</li>
                <li>• Exchange rate: 1 USD = {exchangeRate} credits</li>
                <li>• Instant conversion with no fees</li>
              </ul>
            </div>

            <div className="bg-blue-600/20 border border-blue-500/50 p-4 rounded-lg">
              <h3 className="text-blue-400 font-semibold mb-2">Exchange Rate</h3>
              <div className="text-white text-lg font-mono">
                1 USD = {exchangeRate} credits
              </div>
              <p className="text-blue-300 text-sm mt-1">
                Current system rate
              </p>
            </div>

            <div className="bg-green-600/20 border border-green-500/50 p-4 rounded-lg">
              <h3 className="text-green-400 font-semibold mb-2">Popular Exchanges</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">$1.00</span>
                  <span className="text-white">{exchangeRate} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">$5.00</span>
                  <span className="text-white">{5 * exchangeRate} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">$10.00</span>
                  <span className="text-white">{10 * exchangeRate} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">$25.00</span>
                  <span className="text-white">{25 * exchangeRate} credits</span>
                </div>
              </div>
            </div>

            <div className="bg-purple-600/20 border border-purple-500/50 p-4 rounded-lg">
              <h3 className="text-purple-400 font-semibold mb-2">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                {[1, 5, 10, 25].map(amount => (
                  <button
                    key={amount}
                    onClick={() => {
                      if (amount <= (user?.balance || 0)) {
                        setUsdAmount(amount.toString());
                      }
                    }}
                    disabled={amount > (user?.balance || 0)}
                    className={`p-2 rounded text-sm transition-colors ${
                      amount <= (user?.balance || 0)
                        ? 'bg-purple-600/30 text-purple-400 hover:bg-purple-600/50'
                        : 'bg-gray-600/30 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-yellow-600/20 border border-yellow-500/50 p-4 rounded-lg">
              <h3 className="text-yellow-400 font-semibold mb-2">Important Notes</h3>
              <ul className="text-yellow-300 text-sm space-y-1">
                <li>• Credits cannot be converted back to USD</li>
                <li>• All exchanges are final and irreversible</li>
                <li>• Exchange rate may change based on system settings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Exchange;
